import React, {useCallback} from 'react';

import {CountrySuggest} from 'src/components/country-suggest';
import {FormInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {SmartSection} from 'src/components/smart-section';
import {useCanadianProvinces, useUsStates} from 'src/hooks/dictionaries';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';


import {ContactInformationSkeleton} from './user-contact-information.skeleton';

import type {UserInformation} from 'src/modules/profile/account/create-user/typedef';

type Props = {
    isLoading: boolean,
    setUserAccountInformation: React.Dispatch<React.SetStateAction<UserInformation>>,
    userContactInfo: UserInformation,
    disabled?: boolean,
    onFieldChange: (fieldName: string) => void,
};

const SUPPORTED_STATE_COUNTRIES = ['US', 'CAN'];

export const ContactInformation = (props: Props) => {
    const {isLoading, setUserAccountInformation, userContactInfo, disabled, onFieldChange} = props;

    const {usStates} = useUsStates();
    const {canadianProvinces} = useCanadianProvinces();

    const onChange = useCallback((value: string, name: string) => {
        onFieldChange(name);
        setUserAccountInformation(state => ({...state, [name]: value}));
    }, [onFieldChange, setUserAccountInformation]);

    const onCountrySelect = useCallback((countryCode: string) => {
        onFieldChange('Country');
        setUserAccountInformation(userInfo => ({
            ...userInfo,
            countryCode,
            ...(!countryCode || !SUPPORTED_STATE_COUNTRIES.includes(countryCode) ? {stateProvince: ''} : null),
        }));
    }, [setUserAccountInformation, onFieldChange]);

    const onChangeState = useCallback((stateProvince: string) => {
        onFieldChange('State / Prov');
        setUserAccountInformation(userInfo => ({...userInfo, stateProvince}));
    }, [onFieldChange, setUserAccountInformation]);

    return (
        <SmartSection
            isLoading={isLoading}
            hasLoadingError={false}
            skeletonView={<ContactInformationSkeleton/>}>
            <FormSection headline="User Contact Information">
                <Group direction="v" alignItems="start">
                    <ResponsiveElementGroup>
                        <FormInput
                            label="Work Phone"
                            width={300}
                            readOnly={disabled}
                            maxLength={50}
                            value={userContactInfo.workPhone}
                            onBlur={value => onChange(value, 'workPhone')}
                        />
                        <FormInput
                            label="Mobile Phone"
                            width={300}
                            readOnly={disabled}
                            maxLength={50}
                            value={userContactInfo.mobilePhone}
                            onBlur={value => onChange(value, 'mobilePhone')}
                        />
                        <FormInput
                            label="LinkedIn"
                            width={300}
                            readOnly={disabled}
                            maxLength={255}
                            value={userContactInfo.linkedIn}
                            onBlur={value => onChange(value, 'linkedIn')}
                        />
                        <div style={{width: 300}}>
                            <Label>Country</Label>
                            <CountrySuggest
                                disabled={disabled}
                                selectedCountry={userContactInfo.countryCode}
                                onSelect={onCountrySelect}
                            />
                        </div>
                    </ResponsiveElementGroup>
                    <ResponsiveElementGroup>
                        <FormInput
                            label="Address 1"
                            width={300}
                            readOnly={disabled}
                            maxLength={200}
                            value={userContactInfo.address1}
                            onBlur={value => onChange(value, 'address1')}
                        />
                        <FormInput
                            label="Address 2"
                            width={300}
                            readOnly={disabled}
                            maxLength={200}
                            value={userContactInfo.address2}
                            onBlur={value => onChange(value, 'address2')}
                        />
                    </ResponsiveElementGroup>
                    <ResponsiveElementGroup>
                        <FormInput
                            label="City"
                            width={300}
                            readOnly={disabled}
                            maxLength={50}
                            value={userContactInfo.city}
                            onBlur={value => onChange(value, 'city')}
                        />
                        <div style={{width: 300}}>
                            <Label>State / Prov</Label>
                            {!SUPPORTED_STATE_COUNTRIES.includes(userContactInfo.countryCode) &&
                                <Input disabled={true} placeholder="Select state / prov"/>}
                            {userContactInfo.countryCode === 'CAN' && (
                                <ComboBox
                                    disabled={disabled}
                                    placeholder="Select state / prov"
                                    value={userContactInfo.stateProvince}
                                    onChange={onChangeState}>
                                    {canadianProvinces.map(({code, description}) => (
                                        <ComboboxItem key={code} caption={description} value={code}/>
                                    ))}
                                </ComboBox>
                            )}
                            {userContactInfo.countryCode === 'US' && (
                                <ComboBox
                                    disabled={disabled}
                                    placeholder="Select state / prov"
                                    value={userContactInfo.stateProvince}
                                    onChange={onChangeState}>
                                    {usStates.map(({code, description}) => (
                                        <ComboboxItem key={code} caption={description} value={code}/>
                                    ))}
                                </ComboBox>
                            )}
                        </div>
                        <FormInput
                            label="Postal Code"
                            width={300}
                            readOnly={disabled}
                            maxLength={50}
                            value={userContactInfo.postCode}
                            onBlur={value => onChange(value, 'postCode')}
                        />
                    </ResponsiveElementGroup>
                </Group>
            </FormSection>
        </SmartSection>
    );
};
